import { useGetDashboardSummary, useGetDashboardActivity } from "@workspace/api-client-react";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DemoExportGate } from "@/components/DemoExportGate";
import { Progress } from "@/components/ui/progress";
import {
  AlertCircle, FileText, CheckCircle2, Users, DollarSign,
  TrendingUp, ShieldAlert, AlertTriangle, Clock, ArrowRight, Activity,
  BarChart3, Shield, Building2, ChevronRight, Download, Scale, UserCog, Trophy
} from "lucide-react";
import { format } from "date-fns";
import { useOrg, type Persona } from "@/lib/orgContext";
import { getEffectiveCoachLimit, PLAN_META, type Plan } from "@workspace/plans";
import { useOnboarding } from "@/lib/onboarding";
import { WelcomePanel } from "@/components/WelcomePanel";
import { DemoTour } from "@/components/DemoTour";
import { useDemoMode } from "@/components/DemoBanner";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { CountUp, HoverLift } from "@/components/motion";
import { MaskedValue } from "@/components/MaskedValue";
import { TooltipProvider } from "@/components/ui/tooltip";
import { PersonaBadge } from "@/components/PersonaBadge";
import { SeatUsageBanner } from "@/components/SeatUsageBanner";
import { useActivePersona } from "@/lib/activePersona";

async function triggerDownload(url: string, filename: string) {
  const res = await fetch(url);
  if (!res.ok) throw new Error("Download failed");
  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objUrl);
}

const ACTIVITY_ICONS: Record<string, React.ElementType> = {
  contract_signed: CheckCircle2,
  contract_created: FileText,
  contract_updated: FileText,
  insurance_expired: AlertTriangle,
  insurance_added: Shield,
  note_added: Activity,
  coach_created: Users,
};

const ACTIVITY_COLORS: Record<string, string> = {
  contract_signed: "text-field-green",
  contract_created: "text-primary",
  contract_updated: "text-muted-foreground",
  insurance_expired: "text-signal-red",
  insurance_added: "text-field-green",
  note_added: "text-muted-foreground",
  coach_created: "text-primary",
};

const SETUP_STEPS = [
  {
    number: 1,
    icon: Users,
    label: "Add your coaches",
    description: "Create profiles for each coach on your staff.",
    href: "/coaches/new",
    cta: "Add first coach",
  },
  {
    number: 2,
    icon: FileText,
    label: "Attach contracts",
    description: "Record contract terms, salary, and expiry dates.",
    href: "/contracts/new",
    cta: "New contract",
  },
  {
    number: 3,
    icon: Shield,
    label: "Track compliance",
    description: "Monitor insurance and flag any open risk items.",
    href: "/insurance",
    cta: "View insurance",
  },
];

function StatCard({
  title, value, sub, icon: Icon, color = "default", href,
}: {
  title: string;
  value: React.ReactNode;
  sub?: string;
  icon: React.ElementType;
  color?: "default" | "red" | "orange" | "green" | "blue" | "gold" | "purple";
  href?: string;
}) {
  const valueColorMap: Record<string, string> = {
    default: "text-foreground",
    red: "text-signal-red",
    orange: "text-orange-500",
    green: "text-field-green",
    blue: "text-blue-600 dark:text-blue-400",
    gold: "text-gold",
    purple: "text-purple-600 dark:text-purple-400",
  };
  const iconColorMap: Record<string, string> = {
    default: "text-muted-foreground",
    red: "text-signal-red",
    orange: "text-orange-400",
    green: "text-field-green",
    blue: "text-blue-500",
    gold: "text-gold",
    purple: "text-purple-500",
  };
  const iconBgMap: Record<string, string> = {
    default: "bg-muted",
    red: "bg-signal-red-subtle",
    orange: "bg-orange-500/10",
    green: "bg-field-green-subtle",
    blue: "bg-blue-500/10",
    gold: "bg-gold-subtle",
    purple: "bg-purple-500/10",
  };
  const cardGradientMap: Record<string, string> = {
    default: "",
    red: "kpi-card-red",
    orange: "",
    green: "kpi-card-green",
    blue: "kpi-card-blue",
    gold: "kpi-card-gold",
    purple: "",
  };

  const gradientClass = cardGradientMap[color] ?? "";

  const content = (
    <Card
      className={`transition-all duration-200 animate-count-up card-hover-glow ${gradientClass} ${
        href ? "cursor-pointer hover:shadow-md active:scale-[0.99]" : ""
      } ${href ? "card-lift" : ""}`}
    >
      <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0 p-4">
        <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide leading-tight pr-2">{title}</CardTitle>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBgMap[color]}`}>
          <Icon className={`h-4 w-4 ${iconColorMap[color]}`} />
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <div className={`text-2xl font-bold kpi-value tracking-tight ${valueColorMap[color]}`}>
          {typeof value === "number" ? (
            <CountUp value={value} />
          ) : (
            value
          )}
        </div>
        {sub && <p className="text-xs text-muted-foreground mt-1 leading-tight">{sub}</p>}
      </CardContent>
    </Card>
  );

  return href ? <Link href={href}>{content}</Link> : content;
}

function DashboardEmptyState({
  institutionName,
  hasCoaches,
  hasContracts,
  hasInsurance,
}: {
  institutionName?: string | null;
  hasCoaches: boolean;
  hasContracts: boolean;
  hasInsurance: boolean;
}) {
  const completion: boolean[] = [hasCoaches, hasContracts, hasInsurance];
  const completedCount = completion.filter(Boolean).length;
  const progressPct = (completedCount / SETUP_STEPS.length) * 100;
  const currentStepIdx = completion.findIndex((done) => !done);

  return (
    <Card className="border-dashed border-2 animate-fade-in">
      <CardContent className="py-12 px-4 sm:py-16 sm:px-8">
        <div className="max-w-lg mx-auto text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6 ring-1 ring-inset ring-primary/20">
            <Building2 className="h-8 w-8 text-primary" />
          </div>
          <h2 className="text-xl font-bold tracking-tight mb-2">
            Welcome to Zindeal{institutionName ? `, ${institutionName}` : ""}
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Your command center is ready. Complete these three steps to get your athletic department fully operational.
          </p>
        </div>

        <div className="max-w-2xl mx-auto mb-8 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="font-semibold text-muted-foreground">
              Setup progress
            </span>
            <span className="font-bold text-primary" data-testid="text-setup-progress">
              {completedCount} of {SETUP_STEPS.length} complete
            </span>
          </div>
          <Progress value={progressPct} className="h-2" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 stagger-children">
          {SETUP_STEPS.map((step, idx) => {
            const Icon = step.icon;
            const isDone = completion[idx];
            const isCurrent = !isDone && idx === currentStepIdx;
            const isUpcoming = !isDone && !isCurrent;
            return (
              <Link key={step.number} href={step.href}>
                <div
                  className={`group relative rounded-xl border-2 transition-all p-5 cursor-pointer h-full ${
                    isDone
                      ? "bg-emerald-50/50 border-emerald-200 hover:border-emerald-300"
                      : isCurrent
                      ? "bg-primary/5 border-primary/40 hover:border-primary shadow-sm ring-2 ring-primary/10"
                      : "bg-muted/30 border-dashed border-border hover:bg-muted/60 hover:border-primary/30"
                  }`}
                  data-testid={`setup-step-${step.number}`}
                  data-state={isDone ? "done" : isCurrent ? "current" : "upcoming"}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                        isDone
                          ? "bg-emerald-500 text-white"
                          : isCurrent
                          ? "bg-primary text-primary-foreground"
                          : "bg-primary/10 text-primary group-hover:bg-primary/20"
                      }`}
                    >
                      {isDone ? <CheckCircle2 className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <span className={`text-[10px] font-bold uppercase tracking-widest ${
                        isDone ? "text-emerald-700" : isCurrent ? "text-primary" : "text-muted-foreground/60"
                      }`}>
                        Step {step.number}
                      </span>
                      {isDone && <span className="text-[10px] font-semibold text-emerald-700">Done</span>}
                      {isCurrent && <span className="text-[10px] font-semibold text-primary">Next</span>}
                    </div>
                  </div>
                  <h3 className={`font-semibold text-sm mb-1 ${isUpcoming ? "text-muted-foreground" : ""}`}>
                    {step.label}
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-4">{step.description}</p>
                  <div
                    className={`flex items-center gap-1 text-xs font-semibold transition-all ${
                      isDone
                        ? "text-emerald-700 group-hover:gap-2"
                        : isCurrent
                        ? "text-primary group-hover:gap-2"
                        : "text-muted-foreground group-hover:text-primary group-hover:gap-2"
                    }`}
                  >
                    {isDone ? "View" : step.cta}
                    <ChevronRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

const PERSONA_CONFIG: Record<Persona, {
  label: string;
  description: string;
  icon: React.ElementType;
  color: string;
  links: { label: string; href: string }[];
}> = {
  Finance: {
    label: "Finance View",
    description: "Compensation, buyouts, bonuses, and total cost-of-contract.",
    icon: DollarSign,
    color: "text-emerald-600 dark:text-emerald-400",
    links: [
      { label: "Contract list", href: "/contracts" },
      { label: "Compensation verification", href: "/compensation-verification" },
      { label: "Renewals pipeline", href: "/renewals" },
    ],
  },
  HR: {
    label: "HR View",
    description: "Coach profiles, contract terms, benefits, and onboarding fields.",
    icon: UserCog,
    color: "text-blue-600 dark:text-blue-400",
    links: [
      { label: "Coaches", href: "/coaches" },
      { label: "Renewals", href: "/renewals" },
    ],
  },
  Legal: {
    label: "Legal View",
    description: "Clauses, redlines, policy compliance, and audit trail.",
    icon: Scale,
    color: "text-purple-600 dark:text-purple-400",
    links: [
      { label: "Audit log", href: "/audit-log" },
      { label: "Risk tracker", href: "/mitigations" },
    ],
  },
  SA: {
    label: "Sport Admin View",
    description: "Roster oversight by sport, contract lifecycle, and per-sport approval sign-offs.",
    icon: Trophy,
    color: "text-amber-600 dark:text-amber-400",
    links: [
      { label: "Coaches", href: "/coaches" },
      { label: "Approvals", href: "/approvals" },
      { label: "Renewals", href: "/renewals" },
    ],
  },
  // Athletic Director (top-exec) view: department-wide roll-up rendered
  // as ADRollupPanel below when AD is the active persona.
  AD: {
    label: "Athletic Director View",
    description: "Department-wide spend, headcount by sport, expirations, and approvals routed to AD.",
    icon: Building2,
    color: "text-slate-600 dark:text-slate-400",
    links: [
      { label: "Approvals (AD only)", href: "/approvals?persona=ad" },
      { label: "Reports", href: "/reports" },
    ],
  },
};

const ACTIVE_PERSONA_STORAGE_KEY = "zindeal.activePersona";

const PERSONA_REPORT_URLS: Record<Persona, { csv: string; pdf: string }> = {
  Finance: { csv: "/api/reports/finance.csv", pdf: "/api/reports/finance.pdf" },
  HR: { csv: "/api/reports/hr.csv", pdf: "/api/reports/hr.pdf" },
  Legal: { csv: "/api/reports/legal.csv", pdf: "/api/reports/legal.pdf" },
  // Sport Admins reuse the HR roster export by default; persona-specific
  // exports can be added in a follow-up.
  SA: { csv: "/api/reports/hr.csv", pdf: "/api/reports/hr.pdf" },
  // AD has a tailored department-wide executive summary (Task #389).
  AD: { csv: "/api/reports/ad.csv", pdf: "/api/reports/ad.pdf" },
};

function PersonaReportButtons({ persona }: { persona: Persona }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState<"csv" | "pdf" | null>(null);
  const urls = PERSONA_REPORT_URLS[persona];

  async function handle(format: "csv" | "pdf") {
    setBusy(format);
    try {
      const date = new Date().toISOString().split("T")[0];
      await triggerDownload(urls[format], `${persona.toLowerCase()}-report-${date}.${format}`);
    } catch (err) {
      toast({
        title: "Report download failed",
        description: err instanceof Error ? err.message : "Unable to download report.",
        variant: "destructive",
      });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex gap-1.5">
      <Button
        size="sm"
        variant="outline"
        className="h-7 px-2 text-[11px]"
        disabled={busy !== null}
        onClick={() => handle("csv")}
        data-testid={`persona-export-${persona.toLowerCase()}-csv`}
      >
        <Download className="h-3 w-3 mr-1" />
        {busy === "csv" ? "…" : "CSV"}
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="h-7 px-2 text-[11px]"
        disabled={busy !== null}
        onClick={() => handle("pdf")}
        data-testid={`persona-export-${persona.toLowerCase()}-pdf`}
      >
        <Download className="h-3 w-3 mr-1" />
        {busy === "pdf" ? "…" : "PDF"}
      </Button>
    </div>
  );
}

function PersonaFocusPanel({ personas, summary, financialsMasked, totalValue, totalContracts, lastActivePersona }: {
  personas: Persona[];
  summary: { expiringIn90Days?: number; openMitigations?: number; insuranceIssues?: number } | undefined;
  financialsMasked: boolean;
  totalValue: number;
  totalContracts: number;
  lastActivePersona: Persona | null;
}) {
  if (!personas || personas.length === 0) return null;

  const stored = localStorage.getItem(ACTIVE_PERSONA_STORAGE_KEY) as Persona | null;
  const activePersona = stored && (personas as string[]).includes(stored)
    ? stored
    : (lastActivePersona && (personas as string[]).includes(lastActivePersona) ? lastActivePersona : null);

  const orderedPersonas = activePersona
    ? [activePersona, ...personas.filter((p) => p !== activePersona)]
    : personas;

  return (
    <div className="space-y-3">
      <p className="section-label">Your Persona Focus</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
        {orderedPersonas.map((p) => {
          const cfg = PERSONA_CONFIG[p];
          if (!cfg) return null;
          const Icon = cfg.icon;
          const isActive = activePersona === p;

          const metrics: { label: string; value: React.ReactNode }[] = [];
          if (p === "Finance") {
            metrics.push({ label: "Total contract value", value: financialsMasked ? <MaskedValue /> : `$${(totalValue / 1_000_000).toFixed(2)}M` });
            metrics.push({ label: "Active contracts", value: totalContracts });
          } else if (p === "HR") {
            metrics.push({ label: "Expiring in 90 days", value: summary?.expiringIn90Days ?? 0 });
            metrics.push({ label: "Insurance issues", value: summary?.insuranceIssues ?? 0 });
          } else if (p === "SA") {
            metrics.push({ label: "Expiring in 90 days", value: summary?.expiringIn90Days ?? 0 });
            metrics.push({ label: "Active contracts", value: totalContracts });
          } else if (p === "AD") {
            metrics.push({ label: "Active contracts", value: totalContracts });
            metrics.push({ label: "Expiring in 90 days", value: summary?.expiringIn90Days ?? 0 });
          } else if (p === "Legal") {
            metrics.push({ label: "Open risk items", value: summary?.openMitigations ?? 0 });
            metrics.push({ label: "Expiring contracts", value: summary?.expiringIn90Days ?? 0 });
          }

          const borderColor =
            p === "Finance" ? "rgb(16 185 129)" :
            p === "HR" ? "rgb(59 130 246)" :
            p === "SA" ? "rgb(245 158 11)" :
            p === "AD" ? "rgb(100 116 139)" :
            "rgb(139 92 246)";

          return (
            <Card
              key={p}
              className={`border-l-4 transition-all ${isActive ? "shadow-md ring-1 ring-inset" : ""}`}
              style={{
                borderLeftColor: borderColor,
                ...(isActive ? { ringColor: borderColor } : {}),
              }}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Icon className={`h-4 w-4 ${cfg.color}`} />
                  <span>{cfg.label}</span>
                  <PersonaBadge persona={p} size="xs" />
                  {isActive && <span className="ml-auto text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Active</span>}
                </CardTitle>
                <CardDescription className="text-xs">{cfg.description}</CardDescription>
              </CardHeader>
              <CardContent className="pb-3">
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {metrics.map((m) => (
                    <div key={m.label} className="bg-muted/40 rounded-lg p-2">
                      <div className="text-xs text-muted-foreground truncate">{m.label}</div>
                      <div className="font-bold text-sm mt-0.5">{m.value}</div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  {cfg.links.map((link) => (
                    <Link key={link.href} href={link.href}>
                      <span className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                        {link.label} <ChevronRight className="h-3 w-3" />
                      </span>
                    </Link>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t flex items-center justify-between gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                    {p} Report
                  </span>
                  <PersonaReportButtons persona={p} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// Sport Administrator persona-scoped rollup. Only mounts when SA is the
// active persona; pulls roster-by-sport, expirations 90/180/365, total
// comp and open buyout exposure from the existing /sport-admin/dashboard
// endpoint, scoped server-side to the caller's mapped sports.
interface SportAdminRollupData {
  scope: { sports: string[] | null; allSports: boolean };
  headcount: number;
  activeContracts: number;
  totalAnnualCompensation: number;
  buyoutExposure: number;
  expirations: { d90: number; d180: number; d365: number };
  rosterBySport: Array<{ sport: string; headcount: number; totalComp: number }>;
  upcomingExpirations: Array<{
    contractId: number;
    coachName: string;
    sport: string | null;
    endDate: string | null;
    daysUntil: number;
    annualSalary: number | null;
  }>;
}

function SportAdminRollupPanel({ personas, lastActivePersona }: {
  personas: Persona[];
  lastActivePersona: Persona | null;
}) {
  const stored = typeof window !== "undefined"
    ? (localStorage.getItem(ACTIVE_PERSONA_STORAGE_KEY) as Persona | null)
    : null;
  const activePersona = stored && (personas as string[]).includes(stored)
    ? stored
    : (lastActivePersona && (personas as string[]).includes(lastActivePersona) ? lastActivePersona : null);
  // Only render when SA is the explicitly active persona — keeps Finance/
  // HR/Legal-focused users from seeing an unrelated panel.
  const [data, setData] = useState<SportAdminRollupData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const isActive = activePersona === "SA";

  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetch("/api/sport-admin/dashboard")
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: SportAdminRollupData) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isActive]);

  if (!isActive) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="section-label flex items-center gap-2">
          <Trophy className="h-3.5 w-3.5 text-amber-600" />
          Sport Admin Rollup
          {data?.scope.allSports
            ? <Badge variant="outline" className="text-[10px]">All sports</Badge>
            : data?.scope.sports?.length
              ? <Badge variant="outline" className="text-[10px]">{data.scope.sports.join(", ")}</Badge>
              : null}
        </p>
      </div>
      {loading && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      )}
      {error && (
        <Card><CardContent className="py-3 text-xs text-muted-foreground">Could not load sport admin rollup.</CardContent></Card>
      )}
      {!loading && !error && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="border-l-4" style={{ borderLeftColor: "rgb(245 158 11)" }}>
              <CardContent className="py-3">
                <div className="text-[11px] text-muted-foreground">Coaches in scope</div>
                <div className="text-xl font-bold mt-0.5">{data.headcount}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">{data.activeContracts} active contracts</div>
              </CardContent>
            </Card>
            <Card className="border-l-4" style={{ borderLeftColor: "rgb(245 158 11)" }}>
              <CardContent className="py-3">
                <div className="text-[11px] text-muted-foreground">Total annual comp</div>
                <div className="text-xl font-bold mt-0.5">${(data.totalAnnualCompensation / 1_000_000).toFixed(2)}M</div>
              </CardContent>
            </Card>
            <Card className="border-l-4" style={{ borderLeftColor: "rgb(245 158 11)" }}>
              <CardContent className="py-3">
                <div className="text-[11px] text-muted-foreground">Open buyout exposure</div>
                <div className="text-xl font-bold mt-0.5">${(data.buyoutExposure / 1_000_000).toFixed(2)}M</div>
              </CardContent>
            </Card>
            <Card className="border-l-4" style={{ borderLeftColor: "rgb(245 158 11)" }}>
              <CardContent className="py-3">
                <div className="text-[11px] text-muted-foreground">Expirations</div>
                <div className="text-xs font-bold mt-0.5 flex gap-3">
                  <span>90d: {data.expirations.d90}</span>
                  <span>180d: {data.expirations.d180}</span>
                  <span>365d: {data.expirations.d365}</span>
                </div>
              </CardContent>
            </Card>
          </div>
          {data.rosterBySport.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Roster by sport</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {data.rosterBySport.map((r) => (
                    <div key={r.sport} className="flex items-center justify-between border rounded-md px-3 py-2 text-xs">
                      <span className="font-medium">{r.sport}</span>
                      <span className="text-muted-foreground">
                        {r.headcount} coach{r.headcount === 1 ? "" : "es"}
                        {r.totalComp > 0 && ` · $${(r.totalComp / 1_000_000).toFixed(2)}M`}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

interface ADRollupData {
  headcount: number;
  activeContracts: number;
  totalAnnualCompensation: number;
  buyoutExposure: number;
  expirations: { d90: number; d180: number; d365: number };
  headcountBySport: { sport: string; headcount: number; activeContracts: number; totalComp: number }[];
  topCompensationOutliers: { contractId: number; coachId: number; coachName: string; sport: string | null; annualSalary: number; endDate: string | null }[];
  upcomingExpirations: { contractId: number; coachId: number; coachName: string; sport: string | null; endDate: string | null; daysUntil: number; annualSalary: number | null }[];
  pendingAdEscalations: {
    count: number;
    items: { approvalId: number; contractId: number; coachName: string; sport: string | null; submittedAt: string | null; submittedBy: string | null }[];
  };
}

function ADRollupPanel({ personas, lastActivePersona }: {
  personas: readonly Persona[] | Persona[];
  lastActivePersona: Persona | null;
}) {
  const stored = typeof window !== "undefined"
    ? (localStorage.getItem(ACTIVE_PERSONA_STORAGE_KEY) as Persona | null)
    : null;
  const activePersona = stored && (personas as string[]).includes(stored)
    ? stored
    : (lastActivePersona && (personas as string[]).includes(lastActivePersona) ? lastActivePersona : null);
  const isActive = activePersona === "AD";

  const [data, setData] = useState<ADRollupData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetch("/api/ad/dashboard")
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: ADRollupData) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isActive]);

  if (!isActive) return null;

  const fmtM = (n: number) => `$${(n / 1_000_000).toFixed(2)}M`;
  const fmtK = (n: number) => n >= 1_000_000 ? fmtM(n) : `$${Math.round(n / 1_000).toLocaleString()}K`;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="section-label flex items-center gap-2">
          <Building2 className="h-3.5 w-3.5 text-slate-600" />
          Athletic Director Rollup
          <Badge variant="outline" className="text-[10px]">Department-wide</Badge>
        </p>
        <div className="flex gap-2">
          <PersonaReportButtons persona="AD" />
        </div>
      </div>
      {loading && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      )}
      {error && (
        <Card><CardContent className="py-3 text-xs text-muted-foreground">Could not load AD rollup.</CardContent></Card>
      )}
      {!loading && !error && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="border-l-4 border-l-slate-500">
              <CardContent className="py-3">
                <div className="text-[11px] text-muted-foreground">Coaches</div>
                <div className="text-xl font-bold mt-0.5">{data.headcount}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">{data.activeContracts} active contracts</div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-slate-500">
              <CardContent className="py-3">
                <div className="text-[11px] text-muted-foreground">Total annual comp</div>
                <div className="text-xl font-bold mt-0.5">{fmtM(data.totalAnnualCompensation)}</div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-slate-500">
              <CardContent className="py-3">
                <div className="text-[11px] text-muted-foreground">Buyout exposure</div>
                <div className="text-xl font-bold mt-0.5">{fmtM(data.buyoutExposure)}</div>
              </CardContent>
            </Card>
            <Card className={`border-l-4 ${data.pendingAdEscalations.count > 0 ? "border-l-amber-500" : "border-l-emerald-500"}`}>
              <CardContent className="py-3">
                <div className="text-[11px] text-muted-foreground">Awaiting AD review</div>
                <div className="text-xl font-bold mt-0.5">{data.pendingAdEscalations.count}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  Exp 90/180/365: {data.expirations.d90}/{data.expirations.d180}/{data.expirations.d365}
                </div>
              </CardContent>
            </Card>
          </div>

          {data.headcountBySport.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Headcount &amp; comp by sport</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {data.headcountBySport.map((r) => (
                    <div key={r.sport} className="flex items-center justify-between border rounded-md px-3 py-2 text-xs">
                      <span className="font-medium">{r.sport}</span>
                      <span className="text-muted-foreground">
                        {r.headcount} coach{r.headcount === 1 ? "" : "es"}
                        {r.totalComp > 0 && ` · ${fmtK(r.totalComp)}`}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {data.topCompensationOutliers.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Top comp outliers</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-1">
                  {data.topCompensationOutliers.map((o) => (
                    <Link key={o.contractId} href={`/contracts/${o.contractId}`} className="flex items-center justify-between border rounded-md px-3 py-2 text-xs hover:bg-muted/40">
                      <span>
                        <span className="font-medium">{o.coachName}</span>
                        {o.sport && <span className="text-muted-foreground"> · {o.sport}</span>}
                      </span>
                      <span className="font-mono text-muted-foreground">{fmtK(o.annualSalary)}</span>
                    </Link>
                  ))}
                </CardContent>
              </Card>
            )}

            {data.pendingAdEscalations.items.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    Awaiting your decision
                    <Badge variant="secondary" className="text-[10px]">{data.pendingAdEscalations.count}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-1">
                  {data.pendingAdEscalations.items.slice(0, 8).map((e) => (
                    <Link key={e.approvalId} href={`/contracts/${e.contractId}`} className="flex items-center justify-between border rounded-md px-3 py-2 text-xs hover:bg-muted/40">
                      <span>
                        <span className="font-medium">{e.coachName}</span>
                        {e.sport && <span className="text-muted-foreground"> · {e.sport}</span>}
                      </span>
                      <span className="text-muted-foreground">
                        {e.submittedAt ? format(new Date(e.submittedAt), "MMM d") : "—"}
                      </span>
                    </Link>
                  ))}
                  <div className="pt-1">
                    <Link href="/approvals?persona=ad" className="text-[11px] text-primary hover:underline">View AD approvals →</Link>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { data: summary, isLoading: isLoadingSummary } = useGetDashboardSummary();
  const { data: activity, isLoading: isLoadingActivity } = useGetDashboardActivity();
  const { org } = useOrg();
  const { toast } = useToast();
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const handleDownloadReport = async () => {
    setIsGeneratingPdf(true);
    try {
      await triggerDownload(
        "/api/export/dashboard.pdf",
        `zindeal-report-${new Date().toISOString().split("T")[0]}.pdf`
      );
    } catch {
      toast({ title: "Report generation failed", description: "Unable to generate PDF report. Please try again.", variant: "destructive" });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const totalCoaches = summary?.totalCoaches ?? 0;
  const totalContracts = summary?.totalActiveContracts ?? 0;
  const financialsMasked = !!(summary as { financialsMasked?: boolean } | undefined)?.financialsMasked;
  const totalValue = summary?.totalContractValue ?? 0;
  const avgValue = summary?.averageContractValue ?? 0;

  // Persona-specific dashboard layouts. Finance, HR, and Legal each get a
  // distinct KPI row + lower-grid arrangement; AD/SA and no-persona users
  // fall through to the existing default layout.
  const { effective: activePersona } = useActivePersona(
    org?.personas ?? [],
    org?.lastActivePersona ?? null,
  );
  const personaLayout: "finance" | "hr" | "legal" | null =
    activePersona === "Finance" ? "finance" :
    activePersona === "HR" ? "hr" :
    activePersona === "Legal" ? "legal" :
    null;

  // Onboarding is considered active until ALL three setup milestones are
  // complete (a coach, a contract, and an insurance record). This keeps the
  // stepper — and its done/current/upcoming state — visible after step 1 so
  // users can see what's left rather than having the stepper vanish the
  // moment they add their first coach.
  const hasInsurance = (summary?.activeInsuranceCount ?? 0) > 0;
  const isFullyOnboarded = totalCoaches > 0 && totalContracts > 0 && hasInsurance;
  const isOnboarding = !isLoadingSummary && !isFullyOnboarded;
  // The empty state only fully replaces the dashboard for brand-new orgs
  // (no coaches yet). Partially-onboarded orgs see the stepper above the
  // regular dashboard content so they get progress + their KPIs.
  const isNewOrg = !isLoadingSummary && totalCoaches === 0;
  const currentPlan: Plan = (org?.plan as Plan | undefined) ?? "free";
  const coachLimit = getEffectiveCoachLimit(currentPlan, org?.purchasedSeats ?? 0);
  const planName = PLAN_META[currentPlan as Plan]?.name ?? currentPlan;
  const usagePct = coachLimit ? Math.min(100, Math.round((totalCoaches / coachLimit) * 100)) : 0;
  const nearLimit = coachLimit !== null && totalCoaches >= coachLimit * 0.8;
  // Demo orgs never see seat-usage upsell banners (billing is handled by the
  // Zindeal account team, and pricing must not surface during sales demos).
  const isDemo = useDemoMode();

  return (
    <TooltipProvider>
    <DemoTour enabled={useDemoMode()} />
    <div className="space-y-7 animate-fade-in">
      <WelcomePanel />

      {/* Page header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="page-header">
          {org?.institutionName && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <Building2 className="h-3.5 w-3.5" />
              <span className="font-medium">{org.institutionName}</span>
              {org.conference && <span className="text-muted-foreground/50">· {org.conference}</span>}
            </div>
          )}
          <h1>Dashboard</h1>
          <p>Athletic contract management overview</p>
        </div>
        {!isNewOrg && (
          <div className="flex gap-2">
            <DemoExportGate label="Export Report" size="sm" variant="outline">
              <Button size="sm" variant="outline" onClick={handleDownloadReport} disabled={isGeneratingPdf}>
                <Download className="h-3.5 w-3.5 mr-1.5" />
                {isGeneratingPdf ? "Generating…" : "Export Report"}
              </Button>
            </DemoExportGate>
            {org?.role === "admin" && (
              <>
                <Button asChild size="sm" variant="outline">
                  <Link href="/coaches/new">+ Coach</Link>
                </Button>
                <Button asChild size="sm">
                  <Link href="/contracts/new">+ Contract</Link>
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {!isLoadingSummary && !isNewOrg && org?.personas && org.personas.length > 0 && (
        <PersonaFocusPanel
          personas={org.personas}
          summary={summary as Parameters<typeof PersonaFocusPanel>[0]["summary"]}
          financialsMasked={financialsMasked}
          totalValue={totalValue}
          totalContracts={totalContracts}
          lastActivePersona={org.lastActivePersona ?? null}
        />
      )}

      {!isLoadingSummary && !isNewOrg && org?.personas?.includes("SA") && (
        <SportAdminRollupPanel
          personas={org.personas}
          lastActivePersona={org.lastActivePersona ?? null}
        />
      )}

      {!isLoadingSummary && !isNewOrg && org?.personas?.includes("AD") && (
        <ADRollupPanel
          personas={org.personas}
          lastActivePersona={org.lastActivePersona ?? null}
        />
      )}

      {isLoadingSummary ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => <Skeleton key={i} className="h-28 rounded-xl skeleton-shimmer" />)}
        </div>
      ) : isNewOrg ? (
        <DashboardEmptyState
          institutionName={org?.institutionName}
          hasCoaches={totalCoaches > 0}
          hasContracts={totalContracts > 0}
          hasInsurance={hasInsurance}
        />
      ) : (
        <>
          {/* Partially-onboarded orgs (have a coach but missing contract /
              insurance) still see the stepper above the dashboard so the
              setup flow remains linear and visible until all three steps
              are complete. */}
          {isOnboarding && (
            <DashboardEmptyState
              institutionName={org?.institutionName}
              hasCoaches={totalCoaches > 0}
              hasContracts={totalContracts > 0}
              hasInsurance={hasInsurance}
            />
          )}
          {/* Seat usage warning banner — per-seat plans (track / draft) */}
          {org && !isDemo && (currentPlan === "track" || currentPlan === "draft") && (
            <SeatUsageBanner
              orgId={org.id}
              plan={currentPlan}
              purchasedSeats={org.purchasedSeats ?? 0}
              coachCount={totalCoaches}
            />
          )}

          {/* Generic seat usage card — shown only for non per-seat plans
              (per-seat plans get the dedicated SeatUsageBanner above). */}
          {coachLimit !== null && !isDemo && currentPlan !== "track" && currentPlan !== "draft" && (
            <div className={`rounded-xl border p-4 transition-colors ${nearLimit ? "border-orange-400/50 bg-orange-500/8" : "border-border bg-card"}`}>
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm font-semibold">
                    {planName} plan
                    <span className="font-normal text-muted-foreground ml-2">·</span>
                    <span className={`ml-2 kpi-value font-bold ${nearLimit ? "text-orange-500" : ""}`}>{totalCoaches} of {coachLimit}</span>
                    <span className="text-muted-foreground font-normal ml-1">coach seats used</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {nearLimit
                      ? "Approaching your seat limit — upgrade to keep adding coaches."
                      : "Track your team's usage against the seats included on your plan."}
                  </div>
                </div>
                {nearLimit && (
                  <Button asChild size="sm">
                    <Link href="/settings#plans">Upgrade</Link>
                  </Button>
                )}
              </div>
              <Progress
                value={usagePct}
                className="mt-3 h-1.5"
                indicatorClassName={nearLimit ? "bg-orange-500" : "bg-field-green"}
              />
            </div>
          )}

          {personaLayout === "finance" ? (
            <>
              <div data-tour="dashboard-stats">
                <p className="section-label">Finance — Cost of Contract</p>
                {financialsMasked && (
                  <p className="text-xs text-muted-foreground mb-2 italic">Financial figures are restricted for your role.</p>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children">
                  <StatCard
                    title="Total Contract Obligations"
                    value={financialsMasked ? <MaskedValue /> : `$${(totalValue / 1_000_000).toFixed(2)}M`}
                    sub={`Across ${totalContracts} active contracts`}
                    icon={DollarSign}
                    color="blue"
                    href="/contracts"
                  />
                  <StatCard
                    title="Average Contract Value"
                    value={financialsMasked ? <MaskedValue /> : `$${Math.round(avgValue).toLocaleString()}`}
                    sub="Annual average salary"
                    icon={TrendingUp}
                    color="gold"
                  />
                  <StatCard
                    title="Total Bonus Exposure"
                    value={financialsMasked ? <MaskedValue /> : `$${Math.round(summary?.totalBonusExposure ?? 0).toLocaleString()}`}
                    sub="Active contract bonuses"
                    icon={DollarSign}
                    color="purple"
                    href="/contracts"
                  />
                  <StatCard
                    title="Total Allowance Exposure"
                    value={financialsMasked ? <MaskedValue /> : `$${Math.round(summary?.totalAllowanceExposure ?? 0).toLocaleString()}`}
                    sub="Active contract allowances"
                    icon={DollarSign}
                    color="blue"
                    href="/contracts"
                  />
                </div>
              </div>
              <div>
                <p className="section-label">Renewal Pipeline</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children">
                  <StatCard
                    title="Expiring Within 90 Days"
                    value={summary?.expiringIn90Days ?? 0}
                    sub="Renewal decisions needed"
                    icon={Clock}
                    color={summary?.expiringIn90Days ? "orange" : "default"}
                    href="/renewals"
                  />
                  <StatCard
                    title="Pending Renewals"
                    value={summary?.pendingRenewals ?? 0}
                    sub="Awaiting execution"
                    icon={FileText}
                    color={summary?.pendingRenewals ? "orange" : "default"}
                    href="/renewals"
                  />
                  <StatCard
                    title="Renewed This Month"
                    value={summary?.renewedThisMonth ?? 0}
                    sub="New commitments locked in"
                    icon={CheckCircle2}
                    color="green"
                  />
                  <StatCard
                    title="Active Contracts"
                    value={totalContracts}
                    sub="Currently in force"
                    icon={FileText}
                    color="default"
                    href="/contracts"
                  />
                </div>
              </div>
            </>
          ) : personaLayout === "hr" ? (
            <>
              <div data-tour="dashboard-stats">
                <p className="section-label">HR — Roster Overview</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children">
                  <StatCard
                    title="Active Coaches"
                    value={totalCoaches}
                    sub="Personnel in system"
                    icon={Users}
                    color="blue"
                    href="/coaches"
                  />
                  <StatCard
                    title="Active Contracts"
                    value={totalContracts}
                    sub={summary?.pendingRenewals ? `${summary.pendingRenewals} pending execution` : "All executed"}
                    icon={FileText}
                    color="default"
                    href="/contracts"
                  />
                  <StatCard
                    title="Expiring Within 90 Days"
                    value={summary?.expiringIn90Days ?? 0}
                    sub="Term expirations to plan"
                    icon={Clock}
                    color={summary?.expiringIn90Days ? "orange" : "default"}
                    href="/renewals"
                  />
                  <StatCard
                    title="Insurance Issues"
                    value={summary?.insuranceIssues ?? 0}
                    sub="Onboarding / benefits gaps"
                    icon={ShieldAlert}
                    color={summary?.insuranceIssues ? "red" : "green"}
                    href="/insurance"
                  />
                </div>
              </div>
              <div>
                <p className="section-label">Onboarding Activity</p>
                <div className="grid grid-cols-2 md:grid-cols-2 gap-4 stagger-children">
                  <StatCard
                    title="Renewed This Month"
                    value={summary?.renewedThisMonth ?? 0}
                    sub="Coaches re-signed"
                    icon={CheckCircle2}
                    color="green"
                  />
                  <StatCard
                    title="Pending Renewals"
                    value={summary?.pendingRenewals ?? 0}
                    sub="Awaiting signed paperwork"
                    icon={FileText}
                    color={summary?.pendingRenewals ? "orange" : "default"}
                    href="/renewals"
                  />
                </div>
              </div>
            </>
          ) : personaLayout === "legal" ? (
            <>
              {/* Task #561: Negotiation-pipeline visibility row.
                  Before this lived here, Legal was blind to in-flight deals —
                  the only signal was a tucked-away holder badge on the
                  contracts list. This row puts active counters and legal
                  queue depth at the top of their dashboard. */}
              <div data-tour="legal-negotiation-stats">
                <p className="section-label">Negotiation Pipeline</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children">
                  <StatCard
                    title="In-Flight Negotiations"
                    value={summary?.inFlightNegotiations ?? 0}
                    sub="Out for counter or back from counterparty"
                    icon={Activity}
                    color={(summary?.inFlightNegotiations ?? 0) > 0 ? "purple" : "default"}
                    href="/contracts?lifecycle=out_for_negotiation"
                  />
                  <StatCard
                    title="Counters Awaiting Review"
                    value={summary?.countersAwaitingReview ?? 0}
                    sub="Returned redlines — your move"
                    icon={AlertTriangle}
                    color={(summary?.countersAwaitingReview ?? 0) > 0 ? "orange" : "default"}
                    href="/contracts?lifecycle=counter_received"
                  />
                  <StatCard
                    title="In Legal Review"
                    value={summary?.inLegalReview ?? 0}
                    sub="Parked in your queue"
                    icon={Scale}
                    color={(summary?.inLegalReview ?? 0) > 0 ? "purple" : "default"}
                    href="/contracts?lifecycle=legal_review"
                  />
                  <StatCard
                    title="Approvals Pending"
                    value={summary?.pendingRenewals ?? 0}
                    sub="Awaiting internal sign-off"
                    icon={CheckCircle2}
                    color={(summary?.pendingRenewals ?? 0) > 0 ? "orange" : "default"}
                    href="/contracts?status=pending"
                  />
                </div>
              </div>
              <div data-tour="dashboard-stats">
                <p className="section-label">Legal — Compliance & Risk</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children">
                  <StatCard
                    title="Open Risk Items"
                    value={summary?.openMitigations ?? 0}
                    sub={summary?.criticalMitigations ? `${summary.criticalMitigations} critical — action required` : "No critical items"}
                    icon={AlertTriangle}
                    color={summary?.criticalMitigations ? "red" : summary?.openMitigations ? "orange" : "green"}
                    href="/mitigations"
                  />
                  <StatCard
                    title="Critical Mitigations"
                    value={summary?.criticalMitigations ?? 0}
                    sub="Highest-priority redlines"
                    icon={ShieldAlert}
                    color={summary?.criticalMitigations ? "red" : "default"}
                    href="/mitigations"
                  />
                  <StatCard
                    title="Compliance Alerts"
                    value={summary?.complianceAlerts?.length ?? 0}
                    sub="Open policy / clause issues"
                    icon={AlertCircle}
                    color={summary?.complianceAlerts?.length ? "orange" : "green"}
                  />
                  <StatCard
                    title="Insurance Issues"
                    value={summary?.insuranceIssues ?? 0}
                    sub="Expired or missing policies"
                    icon={Shield}
                    color={summary?.insuranceIssues ? "red" : "green"}
                    href="/insurance"
                  />
                </div>
              </div>
              <div>
                <p className="section-label">Audit & Activity</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children">
                  <StatCard
                    title="Recent Audit Events"
                    value={activity?.length ?? 0}
                    sub="Tracked changes in the feed"
                    icon={Activity}
                    color={activity && activity.length > 0 ? "purple" : "default"}
                    href="/audit-log"
                  />
                  <StatCard
                    title="Active Contracts"
                    value={totalContracts}
                    sub="In-force agreements under review"
                    icon={FileText}
                    color="default"
                    href="/contracts"
                  />
                  <StatCard
                    title="Expiring Within 90 Days"
                    value={summary?.expiringIn90Days ?? 0}
                    sub="Need renewal review"
                    icon={Clock}
                    color={summary?.expiringIn90Days ? "orange" : "default"}
                    href="/contracts"
                  />
                  <StatCard
                    title="Renewed This Month"
                    value={summary?.renewedThisMonth ?? 0}
                    sub="Recently executed"
                    icon={CheckCircle2}
                    color="green"
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Financial KPI row */}
              <div data-tour="dashboard-stats">
                <p className="section-label">Financial Overview</p>
                {financialsMasked && (
                  <p className="text-xs text-muted-foreground mb-2 italic">Financial figures are restricted for your role.</p>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children">
                  <StatCard
                    title="Total Contract Obligations"
                    value={financialsMasked ? <MaskedValue /> : `$${(totalValue / 1_000_000).toFixed(2)}M`}
                    sub={`Across ${totalContracts} active contracts`}
                    icon={DollarSign}
                    color="blue"
                  />
                  <StatCard
                    title="Average Contract Value"
                    value={financialsMasked ? <MaskedValue /> : `$${Math.round(avgValue).toLocaleString()}`}
                    sub="Annual average salary"
                    icon={TrendingUp}
                    color="gold"
                  />
                  <StatCard
                    title="Active Coaches"
                    value={totalCoaches}
                    sub="Personnel in system"
                    icon={Users}
                    color="default"
                    href="/coaches"
                  />
                  <StatCard
                    title="Active Contracts"
                    value={totalContracts}
                    sub={summary?.pendingRenewals ? `${summary.pendingRenewals} pending execution` : "All executed"}
                    icon={FileText}
                    color="default"
                    href="/contracts"
                  />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-2 gap-4 mt-4">
                  <StatCard
                    title="Total Bonus Exposure"
                    value={financialsMasked ? <MaskedValue /> : `$${Math.round(summary?.totalBonusExposure ?? 0).toLocaleString()}`}
                    sub="Sum of bonuses across active contracts"
                    icon={DollarSign}
                    color="purple"
                    href="/contracts"
                  />
                  <StatCard
                    title="Total Allowance Exposure"
                    value={financialsMasked ? <MaskedValue /> : `$${Math.round(summary?.totalAllowanceExposure ?? 0).toLocaleString()}`}
                    sub="Sum of allowances across active contracts"
                    icon={DollarSign}
                    color="blue"
                    href="/contracts"
                  />
                </div>
              </div>

              {/* Compliance KPI row */}
              <div>
                <p className="section-label">Compliance & Risk</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children">
                  <StatCard
                    title="Expiring Within 90 Days"
                    value={summary?.expiringIn90Days ?? 0}
                    sub="Contracts need renewal action"
                    icon={Clock}
                    color={summary?.expiringIn90Days ? "orange" : "default"}
                    href="/contracts"
                  />
                  <StatCard
                    title="Insurance Issues"
                    value={summary?.insuranceIssues ?? 0}
                    sub="Expired or missing policies"
                    icon={ShieldAlert}
                    color={summary?.insuranceIssues ? "red" : "green"}
                    href="/insurance"
                  />
                  <StatCard
                    title="Open Risk Items"
                    value={summary?.openMitigations ?? 0}
                    sub={summary?.criticalMitigations ? `${summary.criticalMitigations} critical — action required` : "No critical items"}
                    icon={AlertTriangle}
                    color={summary?.criticalMitigations ? "red" : summary?.openMitigations ? "orange" : "green"}
                    href="/mitigations"
                  />
                  <StatCard
                    title="Renewed This Month"
                    value={summary?.renewedThisMonth ?? 0}
                    sub="New contracts executed"
                    icon={CheckCircle2}
                    color="green"
                  />
                </div>
              </div>
            </>
          )}
        </>
      )}

      {!isNewOrg && personaLayout === null && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Upcoming renewals */}
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-orange-400" />
                  Upcoming Renewals
                </CardTitle>
                <CardDescription>Contracts expiring within 90 days</CardDescription>
              </div>
              <Button variant="ghost" size="sm" asChild className="text-xs">
                <Link href="/contracts" className="flex items-center gap-1">View all <ArrowRight className="h-3 w-3" /></Link>
              </Button>
            </CardHeader>
            <CardContent>
              {isLoadingSummary ? (
                <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full skeleton-shimmer" />)}</div>
              ) : summary?.upcomingRenewals && summary.upcomingRenewals.length > 0 ? (
                <div className="space-y-1.5 stagger-children">
                  {summary.upcomingRenewals.map((r, i) => {
                    const urgency = r.daysUntilExpiry <= 30 ? "red" : r.daysUntilExpiry <= 60 ? "orange" : "default";
                    return (
                      <div key={i} className="flex items-center justify-between p-3 rounded-lg border bg-muted/20 hover:bg-muted/50 transition-colors">
                        <div>
                          <div className="font-medium text-sm">{r.coachName}</div>
                          <div className="text-xs text-muted-foreground">{r.sport}</div>
                        </div>
                        <div className="text-right">
                          <div className={`text-sm font-bold kpi-value ${urgency === "red" ? "text-signal-red" : urgency === "orange" ? "text-orange-500" : "text-muted-foreground"}`}>
                            {r.daysUntilExpiry}d
                          </div>
                          <div className="text-xs text-muted-foreground">{format(new Date(r.endDate), "MMM d, yyyy")}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-10">
                  <div className="w-12 h-12 rounded-2xl bg-field-green-subtle flex items-center justify-center mx-auto mb-3">
                    <CheckCircle2 className="h-6 w-6 text-field-green" />
                  </div>
                  <div className="text-sm font-medium">All clear</div>
                  <div className="text-xs text-muted-foreground mt-1">No contracts expiring in the next 90 days</div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Compliance alerts */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-signal-red" />
                  Compliance Alerts
                </CardTitle>
                <CardDescription>Items requiring attention</CardDescription>
              </div>
              <Button variant="ghost" size="sm" asChild className="text-xs">
                <Link href="/mitigations" className="flex items-center gap-1">Track <ArrowRight className="h-3 w-3" /></Link>
              </Button>
            </CardHeader>
            <CardContent>
              {isLoadingSummary ? (
                <div className="space-y-3">{[1, 2].map((i) => <Skeleton key={i} className="h-14 w-full skeleton-shimmer" />)}</div>
              ) : summary?.complianceAlerts && summary.complianceAlerts.length > 0 ? (
                <div className="space-y-2 stagger-children">
                  {summary.complianceAlerts.slice(0, 5).map((alert, i) => (
                    <div
                      key={i}
                      className={`text-sm p-3 rounded-lg border ${
                        alert.severity === "critical"
                          ? "bg-signal-red-subtle border-signal-red/20 text-signal-red"
                          : "bg-orange-500/8 text-orange-600 border-orange-200/50"
                      }`}
                    >
                      <div className="font-medium text-xs leading-snug">{alert.description}</div>
                      <div className="text-[10px] mt-0.5 opacity-70">{alert.coachName} · {alert.sport}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10">
                  <div className="w-12 h-12 rounded-2xl bg-field-green-subtle flex items-center justify-center mx-auto mb-3">
                    <Shield className="h-6 w-6 text-field-green" />
                  </div>
                  <div className="text-sm font-medium">Fully compliant</div>
                  <div className="text-xs text-muted-foreground mt-1">All coaches meet coverage requirements</div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {!isNewOrg && personaLayout === null && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Sports breakdown */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2"><BarChart3 className="h-4 w-4 text-muted-foreground" /> Sports Breakdown</CardTitle>
                <CardDescription>Contract value by program</CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingSummary ? (
                <div className="space-y-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
              ) : summary?.sportsBreakdown && summary.sportsBreakdown.length > 0 ? (
                <div className="space-y-3 stagger-children">
                  {summary.sportsBreakdown.slice(0, 8).map((s, i) => {
                    const sportValue = typeof s.totalValue === "number" ? s.totalValue : 0;
                    const pct = !financialsMasked && totalValue > 0 ? (sportValue / totalValue) * 100 : 0;
                    return (
                      <div key={i} className="space-y-1.5">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium">{s.sport}</span>
                          <span className="text-muted-foreground text-xs kpi-value">
                            {s.coachCount} coach{s.coachCount !== 1 ? "es" : ""}
                            {financialsMasked ? <> · <MaskedValue /></> : ` · $${sportValue > 0 ? (sportValue / 1000).toFixed(0) + "K" : "0"}`}
                          </span>
                        </div>
                        <Progress value={pct} className="h-1.5" indicatorClassName="bg-primary" />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground text-sm">No sports data available yet.</div>
              )}
            </CardContent>
          </Card>

          {/* Recent activity */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2"><Activity className="h-4 w-4 text-muted-foreground" /> Recent Activity</CardTitle>
                <CardDescription>Latest system events</CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingActivity ? (
                <div className="space-y-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
              ) : activity && activity.length > 0 ? (
                <div className="space-y-3 stagger-children">
                  {activity.slice(0, 7).map((item) => {
                    const Icon = ACTIVITY_ICONS[item.type] ?? Activity;
                    const iconColor = ACTIVITY_COLORS[item.type] ?? "text-muted-foreground";
                    return (
                      <div key={item.id} className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-lg bg-muted/60 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon className={`h-3.5 w-3.5 ${iconColor}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium leading-tight truncate">{item.description}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">{item.coachName} · {item.sport}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground text-center py-6">No recent activity to display.</div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Persona-specific lower grids. Each layout reuses the same data
          sources as the default dashboard but emphasizes the cards each
          role actually cares about day-to-day. */}
      {!isNewOrg && personaLayout === "finance" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-orange-400" />
                  Renewal Pipeline
                </CardTitle>
                <CardDescription>Upcoming renewal decisions and committed spend</CardDescription>
              </div>
              <Button variant="ghost" size="sm" asChild className="text-xs">
                <Link href="/renewals" className="flex items-center gap-1">View all <ArrowRight className="h-3 w-3" /></Link>
              </Button>
            </CardHeader>
            <CardContent>
              {isLoadingSummary ? (
                <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full skeleton-shimmer" />)}</div>
              ) : summary?.upcomingRenewals && summary.upcomingRenewals.length > 0 ? (
                <div className="space-y-1.5 stagger-children">
                  {summary.upcomingRenewals.map((r, i) => {
                    const urgency = r.daysUntilExpiry <= 30 ? "red" : r.daysUntilExpiry <= 60 ? "orange" : "default";
                    return (
                      <div key={i} className="flex items-center justify-between p-3 rounded-lg border bg-muted/20 hover:bg-muted/50 transition-colors">
                        <div>
                          <div className="font-medium text-sm">{r.coachName}</div>
                          <div className="text-xs text-muted-foreground">{r.sport}</div>
                        </div>
                        <div className="text-right">
                          <div className={`text-sm font-bold kpi-value ${urgency === "red" ? "text-signal-red" : urgency === "orange" ? "text-orange-500" : "text-muted-foreground"}`}>
                            {r.daysUntilExpiry}d
                          </div>
                          <div className="text-xs text-muted-foreground">{format(new Date(r.endDate), "MMM d, yyyy")}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-10">
                  <div className="w-12 h-12 rounded-2xl bg-field-green-subtle flex items-center justify-center mx-auto mb-3">
                    <CheckCircle2 className="h-6 w-6 text-field-green" />
                  </div>
                  <div className="text-sm font-medium">No renewals in window</div>
                  <div className="text-xs text-muted-foreground mt-1">Nothing expires in the next 90 days</div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><BarChart3 className="h-4 w-4 text-muted-foreground" /> Cost by Program</CardTitle>
              <CardDescription>Contract value breakdown by sport</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingSummary ? (
                <div className="space-y-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
              ) : summary?.sportsBreakdown && summary.sportsBreakdown.length > 0 ? (
                <div className="space-y-3 stagger-children">
                  {summary.sportsBreakdown.slice(0, 8).map((s, i) => {
                    const sportValue = typeof s.totalValue === "number" ? s.totalValue : 0;
                    const pct = !financialsMasked && totalValue > 0 ? (sportValue / totalValue) * 100 : 0;
                    return (
                      <div key={i} className="space-y-1.5">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium">{s.sport}</span>
                          <span className="text-muted-foreground text-xs kpi-value">
                            {s.coachCount} coach{s.coachCount !== 1 ? "es" : ""}
                            {financialsMasked ? <> · <MaskedValue /></> : ` · $${sportValue > 0 ? (sportValue / 1000).toFixed(0) + "K" : "0"}`}
                          </span>
                        </div>
                        <Progress value={pct} className="h-1.5" indicatorClassName="bg-primary" />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground text-sm">No sports data available yet.</div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {!isNewOrg && personaLayout === "hr" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-orange-400" />
                  Term Expirations
                </CardTitle>
                <CardDescription>Coach contracts ending within 90 days</CardDescription>
              </div>
              <Button variant="ghost" size="sm" asChild className="text-xs">
                <Link href="/coaches" className="flex items-center gap-1">Roster <ArrowRight className="h-3 w-3" /></Link>
              </Button>
            </CardHeader>
            <CardContent>
              {isLoadingSummary ? (
                <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full skeleton-shimmer" />)}</div>
              ) : summary?.upcomingRenewals && summary.upcomingRenewals.length > 0 ? (
                <div className="space-y-1.5 stagger-children">
                  {summary.upcomingRenewals.map((r, i) => {
                    const urgency = r.daysUntilExpiry <= 30 ? "red" : r.daysUntilExpiry <= 60 ? "orange" : "default";
                    return (
                      <div key={i} className="flex items-center justify-between p-3 rounded-lg border bg-muted/20 hover:bg-muted/50 transition-colors">
                        <div>
                          <div className="font-medium text-sm">{r.coachName}</div>
                          <div className="text-xs text-muted-foreground">{r.sport}</div>
                        </div>
                        <div className="text-right">
                          <div className={`text-sm font-bold kpi-value ${urgency === "red" ? "text-signal-red" : urgency === "orange" ? "text-orange-500" : "text-muted-foreground"}`}>
                            {r.daysUntilExpiry}d
                          </div>
                          <div className="text-xs text-muted-foreground">{format(new Date(r.endDate), "MMM d, yyyy")}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-10">
                  <div className="w-12 h-12 rounded-2xl bg-field-green-subtle flex items-center justify-center mx-auto mb-3">
                    <CheckCircle2 className="h-6 w-6 text-field-green" />
                  </div>
                  <div className="text-sm font-medium">No expirations soon</div>
                  <div className="text-xs text-muted-foreground mt-1">Roster terms are stable for the next 90 days</div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Activity className="h-4 w-4 text-muted-foreground" /> Onboarding & Roster Activity</CardTitle>
              <CardDescription>Recent personnel events</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingActivity ? (
                <div className="space-y-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
              ) : activity && activity.length > 0 ? (
                <div className="space-y-3 stagger-children">
                  {activity.slice(0, 7).map((item) => {
                    const Icon = ACTIVITY_ICONS[item.type] ?? Activity;
                    const iconColor = ACTIVITY_COLORS[item.type] ?? "text-muted-foreground";
                    return (
                      <div key={item.id} className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-lg bg-muted/60 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon className={`h-3.5 w-3.5 ${iconColor}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium leading-tight truncate">{item.description}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">{item.coachName} · {item.sport}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground text-center py-6">No recent activity to display.</div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {!isNewOrg && personaLayout === "legal" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-signal-red" />
                  Compliance & Redlines
                </CardTitle>
                <CardDescription>Open policy issues across active contracts</CardDescription>
              </div>
              <Button variant="ghost" size="sm" asChild className="text-xs">
                <Link href="/mitigations" className="flex items-center gap-1">Risk tracker <ArrowRight className="h-3 w-3" /></Link>
              </Button>
            </CardHeader>
            <CardContent>
              {isLoadingSummary ? (
                <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full skeleton-shimmer" />)}</div>
              ) : summary?.complianceAlerts && summary.complianceAlerts.length > 0 ? (
                <div className="space-y-2 stagger-children">
                  {summary.complianceAlerts.slice(0, 8).map((alert, i) => (
                    <div
                      key={i}
                      className={`text-sm p-3 rounded-lg border ${
                        alert.severity === "critical"
                          ? "bg-signal-red-subtle border-signal-red/20 text-signal-red"
                          : "bg-orange-500/8 text-orange-600 border-orange-200/50"
                      }`}
                    >
                      <div className="font-medium text-xs leading-snug">{alert.description}</div>
                      <div className="text-[10px] mt-0.5 opacity-70">{alert.coachName} · {alert.sport}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10">
                  <div className="w-12 h-12 rounded-2xl bg-field-green-subtle flex items-center justify-center mx-auto mb-3">
                    <Shield className="h-6 w-6 text-field-green" />
                  </div>
                  <div className="text-sm font-medium">Fully compliant</div>
                  <div className="text-xs text-muted-foreground mt-1">No open redlines or coverage gaps</div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2"><Activity className="h-4 w-4 text-muted-foreground" /> Audit Activity</CardTitle>
                <CardDescription>Recent contract changes</CardDescription>
              </div>
              <Button variant="ghost" size="sm" asChild className="text-xs">
                <Link href="/audit-log" className="flex items-center gap-1">Audit log <ArrowRight className="h-3 w-3" /></Link>
              </Button>
            </CardHeader>
            <CardContent>
              {isLoadingActivity ? (
                <div className="space-y-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
              ) : activity && activity.length > 0 ? (
                <div className="space-y-3 stagger-children">
                  {activity.slice(0, 7).map((item) => {
                    const Icon = ACTIVITY_ICONS[item.type] ?? Activity;
                    const iconColor = ACTIVITY_COLORS[item.type] ?? "text-muted-foreground";
                    return (
                      <div key={item.id} className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-lg bg-muted/60 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon className={`h-3.5 w-3.5 ${iconColor}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium leading-tight truncate">{item.description}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">{item.coachName} · {item.sport}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground text-center py-6">No recent activity to display.</div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
    </TooltipProvider>
  );
}
